import { Box, Typography, useTheme, alpha } from '@mui/material'
import { useDealData } from 'api/aws/deals/useDealData'
import NoDataDefault from 'components/ui/no-data-default'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { BoxScroller } from 'styles/elements/styleElements'
import { formatCurrency } from '../DealsKanban/aggregateStageTotals'
import { calculateCSAPaymentSchedule } from './calculateCSAPaymentSchedule'
import { calculatePaymentSchedule } from './calculatePaymentSchedule'

type PaymentRow = {
  month: string
  year: number
  paymentAmount: number | string
  productCount: number
  activeDays: number
  productsAssociated: string[] | string
}

const PaymentSchedule = () => {
  const { PK, SK } = useParams()
  const { pinned } = useLayoutContext()
  const dealId = SK?.split('DEAL_')?.[1]
  const { deal, productRows } = useDealData(PK, SK, dealId)
  const theme = useTheme()
  const dateYear = (dateStr?: string | null): number | null => {
    return dateStr ? dayjs(dateStr).year() : null
  }

  const csaTypes = ['CSA', 'RCSA'] as const
  const isCSA = !!deal?.type && csaTypes.includes(deal.type as any)
  const dealYear = dateYear(deal?.startDate)
  const paymentSchedule: PaymentRow[] = useMemo(() => {
    const raw = isCSA ? calculateCSAPaymentSchedule(deal!, productRows) : calculatePaymentSchedule(productRows)
    return (raw ?? []).map((r: any) => ({
      month: String(r.month ?? ''),
      year: typeof r.year === 'number' ? r.year : Number(r.year ?? 0),
      paymentAmount: typeof r.paymentAmount === 'number' ? r.paymentAmount : Number(r.paymentAmount ?? 0),
      productCount: Number(r.productCount ?? 0),
      activeDays: Number(r.activeDays ?? 0),
      productsAssociated: Array.isArray(r.productsAssociated)
        ? r.productsAssociated
        : String(r.productsAssociated ?? ''),
    }))
  }, [isCSA, deal, productRows])

  const totalContractAmountNum = paymentSchedule.reduce<number>((sum, item) => sum + Number(item.paymentAmount || 0), 0)

  if (!productRows?.length) {
    return (
      <NoDataDefault defaultMessage={!productRows?.length ? 'Please complete contract for payment schedule' : null} />
    )
  }

  const headerCellSx = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: 'text.secondary',
    py: 1,
    px: 1.5,
  }

  const cellSx = {
    fontSize: 13,
    fontWeight: 500,
    color: 'text.primary',
    py: 0.75,
    px: 1.5,
    fontVariantNumeric: 'tabular-nums' as const,
  }

  return (
    <BoxScroller
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        height: pinned ? 'calc(100vh - 185px)' : 'calc(100vh - 122px)',
        transition: 'height 0.3s ease',
        overflowY: 'auto',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 900,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Gradient Header */}
        <Box
          sx={{
            background: theme.palette.background.gradientHeader,
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Payment Schedule</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {paymentSchedule.length} payment{paymentSchedule.length !== 1 ? 's' : ''} &middot;{' '}
              {isCSA ? 'CSA' : 'Standard'} schedule
            </Typography>
          </Box>
          <Box
            sx={{
              textAlign: 'right',
              py: 0.5,
              px: 1.5,
              borderRadius: 1.5,
              bgcolor: 'background.ai_accent_bg',
              border: '1px solid',
              borderColor: 'background.ai_accent_border',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'text.secondary',
              }}
            >
              Total Contract
            </Typography>
            <Typography
              sx={{ fontSize: 15, fontWeight: 700, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCurrency(totalContractAmountNum)}
            </Typography>
          </Box>
        </Box>

        {/* Column Headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 80px 140px 1fr',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.text.primary, 0.02),
          }}
        >
          <Typography sx={headerCellSx}>Month</Typography>
          <Typography sx={headerCellSx}>Year</Typography>
          <Typography sx={{ ...headerCellSx, textAlign: 'right' }}>Payment</Typography>
          <Typography sx={headerCellSx}>Products</Typography>
        </Box>

        {/* Rows */}
        {paymentSchedule.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 140px 1fr',
              borderBottom: index < paymentSchedule.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              transition: 'background-color 0.15s',
              '&:hover': {
                bgcolor: alpha(theme.palette.text.primary, 0.03),
              },
            }}
          >
            <Typography sx={cellSx}>{isCSA ? `Payment ${index + 1}` : item.month}</Typography>
            <Typography sx={cellSx}>{item.year}</Typography>
            <Typography
              sx={{
                ...cellSx,
                textAlign: 'right',
                fontWeight: 600,
                color: Number(item.paymentAmount) > 0 ? 'text.complete' : 'text.primary',
              }}
            >
              {formatCurrency(item.paymentAmount)}
            </Typography>
            <Typography
              sx={{
                ...cellSx,
                fontSize: 12,
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {Array.isArray(item.productsAssociated) ? item.productsAssociated.join(', ') : item.productsAssociated}
            </Typography>
          </Box>
        ))}

        {/* Total Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 80px 140px 1fr',
            borderTop: '2px solid',
            borderColor: 'divider',
            background: theme.palette.background.gradientHeader,
          }}
        >
          <Typography sx={{ ...cellSx, fontWeight: 700, gridColumn: 'span 2' }}>Total Contract Amount</Typography>
          <Typography sx={{ ...cellSx, textAlign: 'right', fontWeight: 700, fontSize: 14, color: 'primary.main' }}>
            {formatCurrency(totalContractAmountNum)}
          </Typography>
          <Typography sx={cellSx} />
        </Box>
      </Box>
    </BoxScroller>
  )
}

export default PaymentSchedule
